/**
 * Shared pack-bundle helpers for PDP and cart.
 * Prices always come from Admin pack variants (1–N); totals use greedy chunks.
 * A cart line may contain multiple grinds so Shopify can apply the product's
 * pack price. The drawer expands the private grind breakdown into visible rows.
 */
window.VanabrewPackPricing = (function () {
  const PRIVATE_BAG_GRINDS_PROP = '_Bag grinds';
  // Legacy carts may still carry this public property.
  const LEGACY_BAG_GRINDS_PROP = 'Bag grinds';

  function formatMoney(amountCents, moneyFormat) {
    const amount = Number(amountCents) / 100;
    const rounded = Math.round(amount * 100) / 100;
    const value = Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(2);
    const format = String(moneyFormat || '₹{{amount}}')
      .replace(/Rs\.?\s*/gi, '₹');
    return format
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, value)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, String(Math.round(amount)))
      .replace(/\{\{\s*amount\s*\}\}/g, value);
  }

  function packChunks(totalPacks, unitSize) {
    const unit = Math.max(1, Number(unitSize) || 4);
    const chunks = [];
    let remaining = Math.max(0, Number(totalPacks) || 0);
    while (remaining > unit) {
      chunks.push(unit);
      remaining -= unit;
    }
    if (remaining > 0) chunks.push(remaining);
    return chunks;
  }

  function priceByPackMap(variants) {
    const map = {};
    (variants || []).forEach((variant) => {
      const packs = Number(variant.packs);
      const price = Number(variant.price);
      if (Number.isFinite(packs) && packs > 0 && Number.isFinite(price)) {
        map[packs] = price;
      }
    });
    return map;
  }

  function unitSizeFromVariants(variants) {
    const sizes = (variants || [])
      .map((variant) => Number(variant.packs))
      .filter((packs) => Number.isFinite(packs) && packs > 0);
    return sizes.length ? Math.max(...sizes) : 4;
  }

  function bundledPriceCents(packCount, variants) {
    const priceByPack = priceByPackMap(variants);
    const unit = unitSizeFromVariants(variants);
    const chunks = packChunks(packCount, unit);
    return chunks.reduce((sum, size) => sum + (Number(priceByPack[size]) || 0), 0);
  }

  function findVariant(variants, packs) {
    return (variants || []).find((variant) => Number(variant.packs) === Number(packs)) || null;
  }

  function grindPropertiesForBags(bags) {
    const cleaned = (bags || []).map((bag) => String(bag || 'Whole bean').trim() || 'Whole bean');
    const unique = [...new Set(cleaned)];
    return {
      Grind: unique.length === 1 ? unique[0] : 'Mixed',
      [PRIVATE_BAG_GRINDS_PROP]: cleaned.join('|'),
    };
  }

  function expandBagGrinds(item, packsPerUnit) {
    const qty = Number(item.quantity) || 1;
    const props = item.properties && typeof item.properties === 'object' ? item.properties : {};
    const grind = String(props.Grind || props.grind || 'Whole bean').trim() || 'Whole bean';
    // Read-only fallback for older cart lines that still have Bag grinds.
    const bagProp =
      props[PRIVATE_BAG_GRINDS_PROP] ||
      props[LEGACY_BAG_GRINDS_PROP] ||
      props.Bag_grinds;
    const bags = [];

    for (let unit = 0; unit < qty; unit += 1) {
      if (bagProp) {
        const listed = String(bagProp)
          .split('|')
          .map((value) => value.trim())
          .filter(Boolean);
        if (listed.length === packsPerUnit) {
          bags.push(...listed);
          continue;
        }
      }

      const fillGrind = grind === 'Mixed' ? 'Whole bean' : grind;
      for (let pack = 0; pack < packsPerUnit; pack += 1) {
        bags.push(fillGrind);
      }
    }

    return bags;
  }

  function packsFromCartItem(item) {
    const opts = item.options_with_values || [];
    const packOpt = opts.find((option) => /^packs?$/i.test(String(option.name || '').trim()));
    if (packOpt) {
      const packs = Number.parseInt(String(packOpt.value), 10);
      if (Number.isFinite(packs) && packs > 0) return packs;
    }

    const fromTitle = Number.parseInt(String(item.variant_title || ''), 10);
    return Number.isFinite(fromTitle) && fromTitle > 0 ? fromTitle : 0;
  }

  function packsFromProductVariant(variant) {
    const opts = variant.options || [];
    for (const value of opts) {
      const packs = Number.parseInt(String(value), 10);
      if (Number.isFinite(packs) && packs > 0 && String(value).trim() === String(packs)) {
        return packs;
      }
    }
    const fromTitle = Number.parseInt(String(variant.title || ''), 10);
    return Number.isFinite(fromTitle) && fromTitle > 0 ? fromTitle : 0;
  }

  function groupBagsByGrind(bags) {
    const groups = [];
    const indexByGrind = new Map();
    (bags || []).forEach((grind) => {
      const key = String(grind || 'Whole bean').trim() || 'Whole bean';
      if (!indexByGrind.has(key)) {
        indexByGrind.set(key, groups.length);
        groups.push({ grind: key, count: 0 });
      }
      groups[indexByGrind.get(key)].count += 1;
    });
    return groups;
  }

  function bundledPriceCentsForBags(bags, variants) {
    return bundledPriceCents((bags || []).length, variants);
  }

  function buildDesiredItems(bags, packVariants) {
    const unit = unitSizeFromVariants(packVariants);
    const chunks = packChunks(bags.length, unit);
    const desired = [];
    let offset = 0;

    // Bundle all grinds for one coffee so Shopify applies the combined price.
    chunks.forEach((size) => {
      const variant = findVariant(packVariants, size);
      if (!variant) {
        throw new Error(`Missing ${size}-pack variant`);
      }

      desired.push({
        id: Number(variant.id),
        quantity: 1,
        properties: grindPropertiesForBags(bags.slice(offset, offset + size)),
      });
      offset += size;
    });

    // Merge identical bundled variant+grind breakdown into quantity.
    const merged = [];
    desired.forEach((item) => {
      const key = `${item.id}:${item.properties.Grind}:${item.properties[PRIVATE_BAG_GRINDS_PROP]}`;
      const existing = merged.find(
        (candidate) =>
          `${candidate.id}:${candidate.properties.Grind}:${candidate.properties[PRIVATE_BAG_GRINDS_PROP]}` === key,
      );
      if (existing) {
        existing.quantity += item.quantity;
        return;
      }
      merged.push(item);
    });

    return merged;
  }

  return {
    formatMoney,
    packChunks,
    bundledPriceCents,
    bundledPriceCentsForBags,
    unitSizeFromVariants,
    findVariant,
    grindPropertiesForBags,
    expandBagGrinds,
    packsFromCartItem,
    packsFromProductVariant,
    groupBagsByGrind,
    buildDesiredItems,
  };
})();
